// Eine Listen-Komponente, drei Sichten. Der mode entscheidet den Filter, nicht das Layout:
//   my        — freigegebene Auftraege in den Arbeitsplaetzen des Nutzers (was ein Mechaniker heute tut)
//   backlog   — alles Offene im Werk (Meister-Uebersicht)
//   approvals — CRTD-Auftraege, die auf Freigabe warten (Meister-Freigabe-Queue)
// Echte Personen-Zuordnung kommt mit echter Auth; der Mock naehert "meine" ueber Arbeitsplaetze an.
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/OrdersList.jsx). Geaendert wurden NUR
// Styling-Idiom (v4-Token -> isDarkMode-Verzweigung), Theme-/i18n-Hook, i18n-Keys (pm.-Praefix) und
// die Import-/API-Pfade. Der StatusBadge wandert ins geteilte ui-Kit (./ui) und wird von dort
// importiert (statt hier exportiert wie im Prototyp).
//
// MONOLITH-API-KONVENTION: `user` zuerst, Antwort ist direkt der JSON-Body (.items).
import React, { useEffect, useState } from 'react';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { listOrders } from '../api';
import { Card, StatusBadge } from './ui';

const MODES = {
  my:        { titleKey: 'pm.orders.myTitle',        filter: (o, scope) => o.status === 'REL' && scope.workCenters.includes(o.workCenter) },
  backlog:   { titleKey: 'pm.orders.backlogTitle',   filter: (o) => o.status === 'CRTD' || o.status === 'REL' },
  approvals: { titleKey: 'pm.orders.approvalsTitle', filter: (o) => o.status === 'CRTD' },
};

function OrdersList({ me, user, mode, onOpen, onBack }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [orders, setOrders] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const cfg = MODES[mode] || MODES.my;

  useEffect(() => {
    // alive-Guard: bei schnellem Moduswechsel darf eine verspaetete Antwort des ALTEN Requests
    // die Liste des neuen Modus nicht ueberschreiben (Effekt-Cleanup entwertet den alten Request).
    let alive = true;
    setOrders(null);
    setLoadError(false);
    // OrdersList laedt ueber das AKTIVE Werk (me.activePlant, fail-closed aus dem Profil-Scope);
    // der mode-Filter approximiert "meine" via workCenters. Rolle mitschicken, damit das
    // serverseitige Scope-Gate gegen dasselbe Profil prueft wie die UI.
    listOrders(user, { plant: me.activePlant }, me.profile.id)
      .then((res) => { if (alive) setOrders((res?.items || []).filter((o) => cfg.filter(o, me.profile.scope))); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, me, user, reloadKey]);

  return (
    <div>
      <button
        onClick={onBack}
        className={`flex items-center gap-1 mb-3 font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
      >
        <ArrowLeft size={18} /> {t('pm.common.back')}
      </button>
      <h2 className={`text-xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t(cfg.titleKey)}</h2>
      {mode === 'approvals' && (
        <p className={`text-sm mb-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('pm.orders.approvalsHint')}</p>
      )}

      {loadError ? (
        <div className="py-10 text-center space-y-3">
          <p className={isDarkMode ? 'text-red-300' : 'text-red-600'}>{t('pm.common.loadError')}</p>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className={`text-sm font-medium underline ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
          >
            {t('pm.common.retry')}
          </button>
        </div>
      ) : orders === null ? (
        <p className={`py-10 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.common.loading')}</p>
      ) : orders.length === 0 ? (
        <p className={`py-10 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.orders.empty')}</p>
      ) : (
        <div className="space-y-2 mt-2">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
            {orders.length} {orders.length === 1 ? t('pm.orders.countOne') : t('pm.orders.countSuffix')}
          </p>
          {orders.map((o) => (
            <button key={o.orderId} onClick={() => onOpen(o.orderId)} className="w-full text-left">
              <Card className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{o.description}</div>
                  <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                    {o.orderId} · {o.workCenter}{o.plannedStart ? ` · ${o.plannedStart}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={o.status} />
                  <ChevronRight className={isDarkMode ? 'text-white/40' : 'text-slate-400'} size={18} />
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrdersList;
